import { Suspense, lazy, useState } from 'react';
import { Check, MapPinOff, X } from 'lucide-react';
import type { WriteOffActDto, WriteOffReviewDto } from '@eombor/contracts';
import { Badge, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { AuthImage } from '../../../shared/ui/AuthImage.js';
import { FileAttachment } from '../../../shared/ui/FileAttachment.js';
import { PhotoLightbox, type LightboxPhoto } from '../../../shared/ui/PhotoLightbox.js';
import { LotUsageCard } from '../../storekeeping/ui/LotUsageList.js';
import { WriteOffStatusBadge } from './WriteOffStatusBadge.js';
import { WriteOffStockLine } from './WriteOffStockLine.js';

/**
 * AKTNING TO'LIQ DALILI — bitta ko'rinish, IKKI tomon uchun.
 *
 * Buxgalter va aktni yuborgan xodim bir xil narsani ko'rishi kerak, va bu
 * ataylab: ular ekran orqali gaplashadi. Rad etish sababini xodim boshqa
 * shaklda ko'rsa, "buxgalter nimani ko'rgan edi?" degan savolga faqat
 * telefon qilib javob topilardi.
 *
 * Farq faqat TUGMALARDA — u oynaning o'zida, bu yerda emas.
 */

/* Leaflet ~150 kB: u faqat lokatsiyali akt ochilganda yuklanadi. Aktlar
   ro'yxati va qolgan barcha ekran xaritasiz ishlaydi. */
const LocationMap = lazy(async () => ({
  default: (await import('../../../shared/ui/LocationMap.js')).LocationMap,
}));

const pad = (value: number): string => String(value).padStart(2, '0');

/** «04.09.2026 14:20» — jurnal, tarix va kartochka bir xil o'qiladi. */
const formatMoment = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return (
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

/** Oxirgi TASDIQ — «kim hisobdan chiqardi» degan savolning javobi. */
const approvalOf = (reviews: readonly WriteOffReviewDto[]): WriteOffReviewDto | null =>
  [...reviews].reverse().find((review) => review.decision === 'APPROVE') ?? null;

const Fact = ({ term, children }: { term: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-xs text-text-faint">{term}</dt>
    <dd className="truncate text-sm text-text">{children}</dd>
  </div>
);

export const WriteOffDetail = ({ data }: { data: WriteOffActDto }) => {
  const [opened, setOpened] = useState<number | null>(null);

  const photos: LightboxPhoto[] = data.photoFileIds.map((fileId) => ({
    id: fileId,
    url: `files/${fileId}`,
    // Suratning O'Z koordinatasi javobda yo'q — akt bittasi bilan
    // yuboriladi va u pastda, xaritada turadi.
    gps: null,
    caption: `${data.docNo} — ${data.nomenclatureName}`,
  }));

  const approval = approvalOf(data.reviews);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <WriteOffStatusBadge status={data.status} />
        {data.requisitionDocNo === null ? null : (
          <Badge tone="neutral" mono>
            {data.requisitionDocNo}
          </Badge>
        )}
      </div>

      {/* TASDIQLAGAN ODAM — tarixning ichida ham bor, lekin bu savol eng
          ko'p beriladigani: «buni kim hisobdan chiqargan?». Javob
          yuqorida, pastga qaramasdan ko'rinadi. */}
      {approval === null ? null : (
        <p
          className={cn(
            'flex flex-wrap items-center gap-2 rounded-[var(--radius-md)] border',
            'border-success/30 bg-success-soft px-3 py-2 text-sm text-text',
          )}
        >
          <Check size={14} aria-hidden className="text-success" />
          <span className="font-medium">{t('writeOff.approvedBy')}:</span>
          <span>{approval.reviewerName ?? '—'}</span>
          <span className="tabular text-xs text-text-muted">
            {formatMoment(approval.decidedAt)}
          </span>
        </p>
      )}

      <WriteOffStockLine
        act={data}
        className="rounded-[var(--radius-md)] border border-border bg-surface-2 px-3 py-2 text-sm text-pretty text-text"
      />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Fact term={t('writeOff.subdivision')}>{data.subdivisionName}</Fact>
        <Fact term={t('writeOff.author')}>{data.createdByName ?? '—'}</Fact>
        <Fact term={t('writeOff.requisition')}>
          <span className="tabular">{data.requisitionDocNo ?? '—'}</span>
        </Fact>
        <Fact term={t('writeOff.submittedAt')}>
          <span className="tabular">{formatMoment(data.submittedAt)}</span>
        </Fact>
        <Fact term={t('writeOff.batch')}>
          <span className="tabular">{data.batchNo}</span>
        </Fact>
      </dl>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-text">{t('writeOff.reason')}</span>
        <p className="text-sm text-pretty text-text-muted">{data.reason}</p>
      </div>

      <FileAttachment fileId={data.actFileId} label={t('writeOff.actFile')} />

      {/*
        RASMSIZ AKT — ilovada olingan mahsulot uchun surat so'ralmaydi
        (`writeoff/domain/availability.ts`). Unda tasdiqlovchi bo'sh
        «Suratlar» bo'limini emas, DALILNING O'ZINI ko'radi: kim, qachon,
        nechta va o'z so'zlari bilan nima uchun olgan.
      */}
      {photos.length === 0 && data.usages.length > 0 ? (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text">{t('writeOff.usageTitle')}</span>
          <p className="text-sm text-text-muted">{t('writeOff.photoInstead')}</p>
          <ul className="flex flex-col gap-2">
            {data.usages.map((usage) => (
              <LotUsageCard key={usage.id} item={{ ...usage, unit: data.unit }} />
            ))}
          </ul>
        </div>
      ) : null}

      {photos.length === 0 ? null : (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text">{t('writeOff.photos')}</span>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {photos.map((photo, index) => (
              <li key={photo.id}>
                {/* Eskiz BOSILADI: telefon bilan olingan surat kichkina
                    katakda ko'rinmaydi va buxgalter aynan shu suratdan
                    "ishlatilganmi?" degan xulosa chiqaradi. */}
                <button
                  type="button"
                  onClick={() => setOpened(index)}
                  className={cn(
                    'block w-full overflow-hidden rounded-[var(--radius-sm)] border border-border',
                    'transition-[border-color] duration-[var(--motion-fast)]',
                    'hover:border-border-strong',
                  )}
                >
                  <AuthImage
                    src={photo.url}
                    alt={t('writeOff.photos')}
                    className="h-32 w-full object-cover"
                    skeletonClassName="h-32"
                  />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-text">{t('writeOff.location')}</span>
        {data.locationLat === null || data.locationLng === null ? (
          <p className="flex items-center gap-2 text-sm text-text-muted">
            <MapPinOff size={14} aria-hidden />
            {t('writeOff.noLocation')}
          </p>
        ) : (
          <Suspense fallback={<Skeleton className="h-56 w-full" />}>
            <LocationMap
              lat={data.locationLat}
              lng={data.locationLng}
              accuracy={data.locationAccuracy}
            />
          </Suspense>
        )}
      </div>

      {/* KO'RIB CHIQISH TARIXI — akt qayta yuborilgan bo'lsa, u avval nima
          uchun qaytarilgani shu yerda qoladi. */}
      {data.reviews.length === 0 ? null : (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text">{t('writeOff.history')}</span>
          <ul className="flex flex-col gap-1.5">
            {data.reviews.map((entry) => (
              <li
                key={entry.id}
                className={cn(
                  'flex flex-wrap items-baseline gap-2 rounded-[var(--radius-sm)]',
                  'border border-border bg-surface-2 px-3 py-2 text-sm',
                )}
              >
                <span
                  aria-hidden
                  className={entry.decision === 'APPROVE' ? 'text-success' : 'text-danger'}
                >
                  {entry.decision === 'APPROVE' ? <Check size={14} /> : <X size={14} />}
                </span>
                <span className="font-medium text-text">
                  {entry.decision === 'APPROVE'
                    ? t('writeOff.status.APPROVED')
                    : t('writeOff.status.REJECTED')}
                </span>
                <span className="text-text-muted">{entry.reviewerName ?? '—'}</span>
                <span className="tabular text-xs text-text-faint">
                  {formatMoment(entry.decidedAt)}
                </span>
                {entry.reason === null ? null : (
                  <span className="w-full text-text-muted">{entry.reason}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <PhotoLightbox
        photos={photos}
        index={opened}
        onIndexChange={setOpened}
        onClose={() => setOpened(null)}
      />
    </div>
  );
};
